import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

// Static fallbacks
import { SITE as S_SITE, I18N as S_I18N } from "@/data/site";
import { SERVICES as S_SERVICES, MARQUEE_SERVICES as S_MARQUEE, PILLARS as S_PILLARS, DEPARTMENTS as S_DEPTS, OPTIONS as S_OPTS } from "@/data/services";
import { CASES as S_CASES, CASE_CATEGORIES as S_CC } from "@/data/cases";
import { INSIGHTS as S_INSIGHTS, INSIGHT_TOPICS as S_IT } from "@/data/insights";
import { TEAM as S_TEAM, REASONS as S_REASONS, FOUNDERS as S_FOUNDERS } from "@/data/team";
import { CLIENTS as S_CLIENTS, CLIENT_CATEGORIES as S_CCAT } from "@/data/clients";
import { HISTORY as S_HISTORY } from "@/data/history";
import { STATS as S_STATS, TIMELINE as S_TIMELINE } from "@/data/stats";
import { BRANDING as S_BRANDING, ANALYTICS as S_ANALYTICS, FOOTER_CONFIG as S_FOOTER, ECOSYSTEM as S_ECOSYSTEM, PRESENCE_EXTENDED as S_PRESENCE, UTM_TEMPLATES as S_UTM, SOBRE as S_SOBRE, SOBRE_VIDEOS as S_SOBRE_VIDEOS, TESTIMONIALS as S_TESTIMONIALS, WE_ARE_THUMBNAILS as S_WE_ARE_THUMBS, CONTACT_EXTRAS as S_CONTACT_EXTRAS, AUTHOR_BIOS as S_AUTHOR_BIOS, REMUNERATION_OPTIONS as S_REMUNERATION, CERTIFICATIONS as S_CERTS, SEO_DEFAULTS as S_SEO } from "@/data/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaults = {
  site: S_SITE, i18n: S_I18N, stats: S_STATS, timeline: S_TIMELINE, pillars: S_PILLARS, reasons: S_REASONS,
  marquee: S_MARQUEE, case_categories: S_CC, insight_topics: S_IT, departments: S_DEPTS,
  service_options: S_OPTS, client_categories: S_CCAT,
  home_sections: { founders: S_FOUNDERS, cta_final_title: "", cta_final_subtitle: "", cta_final_font_size: 6 },
  cases: S_CASES, insights: S_INSIGHTS, services: S_SERVICES, team: S_TEAM, clients: S_CLIENTS, history: S_HISTORY,
  branding: S_BRANDING, analytics: S_ANALYTICS, footer_config: S_FOOTER, ecosystem: S_ECOSYSTEM,
  presence_extended: S_PRESENCE, utm_templates: S_UTM, sobre: S_SOBRE, sobre_videos: S_SOBRE_VIDEOS,
  testimonials: S_TESTIMONIALS, contact_extras: S_CONTACT_EXTRAS, we_are_thumbnails: S_WE_ARE_THUMBS,
  author_bios: S_AUTHOR_BIOS, remuneration_options: S_REMUNERATION, certifications: S_CERTS, seo: S_SEO,
};

const ContentCtx = createContext(defaults);

const KV_KEYS = ["site", "i18n", "stats", "timeline", "pillars", "reasons", "marquee", "case_categories", "insight_topics", "departments", "service_options", "remuneration_options", "client_categories", "home_sections", "branding", "analytics", "footer_config", "ecosystem", "presence_extended", "utm_templates", "sobre", "sobre_videos", "testimonials", "contact_extras", "we_are_thumbnails", "author_bios", "certifications", "seo", "popups", "concierge_questions", "verticals", "padroes", "case_projects", "team_roles", "countries", "brazil_states_map", "international_presence", "pillar_icons", "careers_config", "timeline_38", "cases_config", "chatbot_config", "insights_config"];
const COLLS = ["cases", "insights", "services", "team", "founders", "clients", "history", "jobs", "careers_videos"];

export function ContentProvider({ children }) {
  const [content, setContent] = useState(defaults);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = {};
        await Promise.all([
          ...KV_KEYS.map(async (k) => {
            try { const { data } = await axios.get(`${API}/content/kv/${k}`); if (data) out[k] = data; }
            catch (e) { console.warn(`content/kv/${k} hydration failed`, e?.message); }
          }),
          ...COLLS.map(async (k) => {
            try { const { data } = await axios.get(`${API}/content/${k}`); if (Array.isArray(data) && data.length) out[k] = data; }
            catch (e) { console.warn(`content/${k} hydration failed`, e?.message); }
          }),
        ]);
        if (!cancelled && Object.keys(out).length) {
          // Deep-ish merge: for KV objects, merge per-key with defaults so partial KV docs don't strip required fields.
          setContent((prev) => {
            const next = { ...prev };
            for (const k of Object.keys(out)) {
              const v = out[k];
              if (v && typeof v === 'object' && !Array.isArray(v) && prev[k] && typeof prev[k] === 'object' && !Array.isArray(prev[k])) {
                // shallow merge top-level (e.g. site, i18n.pt/en/es) with default as base
                const merged = { ...prev[k] };
                for (const subKey of Object.keys(v)) {
                  const subVal = v[subKey];
                  if (subVal && typeof subVal === 'object' && !Array.isArray(subVal) && merged[subKey] && typeof merged[subKey] === 'object' && !Array.isArray(merged[subKey])) {
                    merged[subKey] = { ...merged[subKey], ...subVal };
                  } else {
                    merged[subKey] = subVal;
                  }
                }
                next[k] = merged;
              } else {
                next[k] = v;
              }
            }
            return next;
          });
        }
      } catch (e) { console.warn("Content hydration outer error", e); }
    })();
    return () => { cancelled = true; };
    // Hydration runs exactly once at mount. API/COLLS/KV_KEYS are module-scoped constants and never change.
  }, []);

  return <ContentCtx.Provider value={content}>{children}</ContentCtx.Provider>;
}

export const useContent = () => useContext(ContentCtx);
